import React, { useState } from 'react'
import { Col, Container, Row } from 'react-bootstrap'

import { selectAccountId, selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { useAppSelector } from '../../../store/hooks'
import { useGetUserByIdQuery, useGetAllUserShareSettingsQuery } from '../../../store/user/user'

import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

import './Collaboration.scss'
import { SharingControl } from './SharingControl'
import { Divider } from '../../../components/Divider/Divider'
import { CollaborationUpsell } from './CollaborationUpsell'
import { EnterpriseEnforcedInfoBox } from './EnterpriseEnforcedInfoBox'

const CollaborationSettingsLoading: React.FC = () => {
  return <div className='skeleton' style={{ width: '100%', height: '2rem' }} />
}

export const CollaborationTab: React.FC = () => {
  const accountId = useAppSelector(selectAccountId)
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const { hasQuota, isFetching: quotaLoading } = useZocksQuota()

  const sessionSharingAvailable = hasQuota('identity.sessionSharingEnabled.value')
  const documentSharingAvailable = hasQuota('ai.documentSummaryAgent.value')
  const schedulingSharingAvailable = hasQuota('scheduling.enabled.value')

  const { data: userData, isFetching: userLoading } = useGetUserByIdQuery({ accountId, userId: currentUserId }, { refetchOnMountOrArgChange: true })
  const { data: contactShareSettings } = useGetAllUserShareSettingsQuery({ accountId, userId: currentUserId, shareType: 'contact' })

  const [contactSharingOverride, setContactSharingOverride] = useState<boolean | null>(null)

  if (quotaLoading || userLoading) {
    return <CollaborationSettingsLoading />
  }

  if (!sessionSharingAvailable) {
    return <CollaborationUpsell />
  }

  if (!userData?.data || !userData.data.effectiveSettings || !userData.data.settings) {
    return null
  }

  const isEnterpriseEnforced =
    userData.data.enterpriseSettings?.contactSharingEnabled != null || userData.data.enterpriseSettings?.sessionSharingEnabled != null

  const contactSharingEffectiveValue = userData.data.effectiveSettings.contactSharingEnabled ?? false

  if (contactSharingOverride !== null && contactSharingOverride === contactSharingEffectiveValue) {
    setContactSharingOverride(null)
  }

  const contactSharingOn = contactSharingOverride ?? contactSharingEffectiveValue

  const documentEnterpriseEnforced = userData.data.enterpriseSettings?.documentSharingEnabled != null
  const documentSharingDisabled = documentEnterpriseEnforced || !contactSharingOn
  const schedulingEnterpriseEnforced = userData.data.enterpriseSettings?.schedulingSharingEnabled != null
  const schedulingSharingDisabled = schedulingEnterpriseEnforced || !contactSharingOn
  const contactAllowedUserIds = contactShareSettings?.data?.length ? contactShareSettings.data.map((share) => share.sharedWithUserId) : undefined

  return (
    <Container className='p-0'>
      <Row>
        <Col xs={12} lg={9} className='d-flex flex-column gap-4'>
          {isEnterpriseEnforced && <EnterpriseEnforcedInfoBox />}
          <SharingControl
            userEffectiveSettings={userData.data.effectiveSettings}
            type='session'
            title='Meeting Sharing'
            toggleTitle='I want to share my meetings with my team members'
            userShareTitle='Specify access to your meetings for selected users.'
            userShareDescription='Check each user to grant individual access to your meetings. If all checkboxes remain unchecked, your meetings will be shared with all teammates.'
            informationBoxText='Meeting participants are visible to anyone who can access your meetings.'
            selectorEmptyText='All users can discover your meetings'
            hideSelector={isEnterpriseEnforced}
            isDisabled={isEnterpriseEnforced}
          />
          <Divider className='d-none d-md-block' />
          <SharingControl
            userEffectiveSettings={userData.data.effectiveSettings}
            type='contact'
            title='Contact Sharing'
            toggleTitle='I want to share my contacts with my team members'
            userShareTitle='Specify access to your contacts for selected users.'
            userShareDescription='Check each user to grant individual access to your contacts. If all checkboxes remain unchecked, your contacts will be shared with all teammates.'
            selectorEmptyText='All users can discover your contacts'
            hideSelector={isEnterpriseEnforced}
            isDisabled={isEnterpriseEnforced}
            onToggleChange={setContactSharingOverride}
          />
          <Divider className='d-none d-md-block' />
          {documentSharingAvailable ? (
            <>
              <SharingControl
                userEffectiveSettings={userData.data.effectiveSettings}
                type='document'
                title='Document Sharing'
                toggleTitle='I want to share my documents with my team members'
                userShareTitle='Specify access to your documents for selected users.'
                userShareDescription='Check each user to grant individual access to your documents. If all checkboxes remain unchecked, your documents will be shared with all teammates in the list. You can only share documents with those members who got access to your contacts as well.'
                selectorEmptyText='All users can discover your documents'
                hideSelector={documentSharingDisabled}
                isDisabled={documentSharingDisabled}
                helperText={!contactSharingOn ? 'Enable contact sharing to control their document sharing options.' : undefined}
                allowedUserIds={contactAllowedUserIds}
              />
              <Divider className='d-none d-md-block' />
            </>
          ) : null}
          {schedulingSharingAvailable ? (
            <>
              <SharingControl
                userEffectiveSettings={userData.data.effectiveSettings}
                type='scheduling'
                title='Scheduling Sharing'
                toggleTitle='I want to let my team members book meetings on my behalf'
                userShareTitle='Specify who can book meetings on your behalf.'
                userShareDescription='Check each user to allow them to book meetings on your calendar. If all checkboxes remain unchecked, all teammates in the list can book on your behalf. You can only share scheduling with those members who got access to your contacts as well.'
                selectorEmptyText='All users can book meetings on your behalf'
                hideSelector={schedulingSharingDisabled}
                isDisabled={schedulingSharingDisabled}
                helperText={!contactSharingOn ? 'Enable contact sharing to control their scheduling sharing options.' : undefined}
                allowedUserIds={contactAllowedUserIds}
              />
              <Divider className='d-none d-md-block' />
            </>
          ) : null}
        </Col>
      </Row>
    </Container>
  )
}
